import React, { useState, useEffect, useRef } from 'react';
import './EventsPreview.css';

const API_HOST = process.env.REACT_APP_API_HOST;
const VISIBLE_CARDS = 8;

function EventsPreview() {
  const [games, setGames] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [backgroundOpacity, setBackgroundOpacity] = useState(1);

  const startTouch = useRef(0);
  const moveTouch = useRef(0);
  const startTime = useRef(0);
  const dragging = useRef(false);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const res = await fetch(`${API_HOST}/public/games`);
        const allGames = await res.json();
        const tempGames = allGames.filter(game => !game.keywords || !game.keywords.includes('archived'));
        const activeGames = tempGames.filter(game => game.keywords.includes('Paintball') && game.keywords.includes('Featured'));

        const gamesWithFlyers = await Promise.all(
          activeGames.map(async (game) => {
            if (game.flyer) {
              try {
                const flyerRes = await fetch(`${API_HOST}/public/images/signed-url/${game.flyer}`);
                const flyerData = await flyerRes.json();
                return { ...game, flyerUrl: flyerData.url };
              } catch {
                return { ...game, flyerUrl: null };
              }
            }
            return { ...game, flyerUrl: null };
          })
        );

        setGames(gamesWithFlyers);
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    fetchGames();
  }, []);

  useEffect(() => {
    if (!autoPlay || games.length <= 1) return;

    const interval = setInterval(() => {
      triggerIndexChange((currentIndex + 1) % games.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoPlay, games.length, currentIndex]);

  const triggerIndexChange = (newIndex) => {
    setBackgroundOpacity(0);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setBackgroundOpacity(1);
    }, 300);
  };

  const handleTouchStart = (e) => {
    startTouch.current = e.touches[0].clientX;
    moveTouch.current = 0;
    startTime.current = Date.now();
    dragging.current = true;
    setAutoPlay(false);
  };

  const handleTouchMove = (e) => {
    if (!dragging.current) return;
    moveTouch.current = e.touches[0].clientX - startTouch.current;
  };

  const handleTouchEnd = () => {
    if (!dragging.current) return;

    const elapsed = Date.now() - startTime.current;
    const velocity = moveTouch.current / elapsed;

    let skipCount = 1;
    if (Math.abs(velocity) > 0.6) {
      skipCount = 3;
    } else if (Math.abs(velocity) > 0.3) {
      skipCount = 2;
    }

    if (moveTouch.current > 30) {
      triggerIndexChange((currentIndex - skipCount + games.length) % games.length);
    } else if (moveTouch.current < -30) {
      triggerIndexChange((currentIndex + skipCount) % games.length);
    } else {
      // Minimal swipe -> snap back to center
      triggerIndexChange(currentIndex);
    }

    startTouch.current = 0;
    moveTouch.current = 0;
    dragging.current = false;
  };

  return (
    <div className="events-preview-wrapper">
      {/* Parallax Background */}
      {games[currentIndex]?.flyerUrl && (
        <div
          className="parallax-background"
          style={{
            backgroundImage: `url(${games[currentIndex].flyerUrl})`,
            opacity: backgroundOpacity,
            transition: 'opacity 0.1s ease',
          }}
        />
      )}

      <div className="carousel-wrapper">
        <div
          className="carousel-window"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="carousel-track">
            {games.map((game, index) => {
              const diff = (index - currentIndex + games.length) % games.length;
              let offset = diff;
              if (offset > games.length / 2) offset -= games.length;

              const CARD_WIDTH = 320;
              const SPACING = 140;
              const shift = offset * SPACING - (CARD_WIDTH / 2);

              const style = {
                transform: `translateX(${shift}px) scale(${offset === 0 ? 1 : 0.8}) rotateY(${offset * 8}deg)`,
                zIndex: 100 - Math.abs(offset),
                opacity: Math.abs(offset) > VISIBLE_CARDS / 2 ? 0 : 1,
              };

              return (
                <div
                  key={game._id || index}
                  className={`carousel-card ${index === currentIndex ? 'active-card' : ''}`}
                  style={style}
                >
                  {game.flyerUrl ? (
                    <img src={game.flyerUrl} alt={game.name} className="carousel-image" />
                  ) : (
                    <div className="carousel-placeholder">No Image</div>
                  )}
                  <h3 className="carousel-title">{game.name}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="dots-container">
        {games.map((_, index) => (
          <div
            key={index}
            className={`dot ${index === currentIndex ? 'active-dot' : ''}`}
            onClick={() => {
              triggerIndexChange(index);
              setAutoPlay(false);
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default EventsPreview;
